import React from 'react';
import { Menu, MenuProps } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  HomeOutlined,
  UserOutlined,
  TeamOutlined,
  BookOutlined,
  BarChartOutlined,
  SettingOutlined,
} from '@ant-design/icons';

type MenuItem = Required<MenuProps>['items'][number];

interface NavbarProps {
  mode?: 'inline' | 'horizontal' | 'vertical';
  theme?: 'light' | 'dark';
  style?: React.CSSProperties;
  collapsed?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ 
  mode = 'inline', 
  theme = 'light', 
  style,
  collapsed = false 
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Menu items for navigation
  const menuItems: MenuItem[] = [
    {
      key: '/',
      icon: <HomeOutlined />,
      label: 'Dashboard',
    },
    {
      key: 'clients',
      icon: <UserOutlined />,
      label: 'Clients',
      children: [
        {
          key: '/clients',
          label: 'Manage Clients',
        },
        {
          key: '/clients/onboarding',
          label: 'Onboarding Pipeline',
        },
      ],
    },
    {
      key: 'coaches',
      icon: <TeamOutlined />,
      label: 'Coaches',
      children: [
        {
          key: '/coaches',
          label: 'Manage Coaches',
        },
        {
          key: '/coaches/analytics',
          label: 'Performance Analytics',
        },
      ],
    },
    {
      key: 'libraries',
      icon: <BookOutlined />,
      label: 'Libraries',
      children: [
        {
          key: '/libraries/exercises',
          label: 'Exercise Library',
        },
        {
          key: '/libraries/equipment',
          label: 'Equipment Library',
        },
        {
          key: '/libraries/adaptations',
          label: 'Adaptations Library',
        },
        {
          key: '/libraries/muscle-groups',
          label: 'Muscle Groups Library',
        },
        {
          key: '/libraries/statistics',
          label: 'Library Statistics',
        },
      ],
    },
    {
      key: 'analytics',
      icon: <BarChartOutlined />,
      label: 'Business Analytics',
      children: [
        {
          key: '/analytics/clients',
          label: 'Client Analytics',
        },
        {
          key: '/analytics/revenue',
          label: 'Revenue Dashboard',
        },
        {
          key: '/analytics/reports',
          label: 'Performance Reports',
        },
      ],
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'System Settings',
      children: [
        {
          key: '/settings/users',
          label: 'User Management',
        },
        {
          key: '/settings/configuration',
          label: 'System Configuration',
        },
        {
          key: '/settings/data',
          label: 'Data Export/Import',
        },
      ],
    },
  ];

  // Handle menu click navigation
  const handleMenuClick: MenuProps['onClick'] = (e) => {
    navigate(e.key);
  };

  // Get current selected keys based on location
  const getSelectedKeys = () => {
    const path = location.pathname;
    if (path === '/') return ['/'];
    return [path];
  };

  // Get opened keys for submenus
  const getOpenKeys = () => {
    const path = location.pathname;
    if (path.startsWith('/clients')) return ['clients'];
    if (path.startsWith('/coaches')) return ['coaches'];
    if (path.startsWith('/libraries')) return ['libraries'];
    if (path.startsWith('/analytics')) return ['analytics'];
    if (path.startsWith('/settings')) return ['settings'];
    return [];
  };

  return (
    <Menu
      mode={mode}
      theme={theme}
      selectedKeys={getSelectedKeys()}
      defaultOpenKeys={collapsed ? [] : getOpenKeys()}
      items={menuItems}
      onClick={handleMenuClick}
      style={style}
    />
  );
};

export default Navbar; 